import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { BroadcastApiModel, ReadReceiptEntry, RecipientAssignment, Broadcast, BroadcastRecipient, BroadcastType, AddEditArgs, IframeApiModel } from "src/shared/models/broadcasts.model";

@Injectable({
    providedIn: "root",
})
export class AnnouncementsService {
    private modes = new BehaviorSubject<AddEditArgs>({} as any)
    currentModes = this.modes.asObservable();
    public progressPopupOpen: boolean = false;
    public folders: any = null;
    public announcementsList: any = [];
    public loadingRecipients: boolean = false;
    public allowShowDate = true;
    public majorMinorForced = false;
    public minorMajorForced = false;

    public save: Broadcast = {
        BroadcastType: 1,
        BroadcastID: null,
        Title: "",
        BroadcastText: "",
        AnnouncementType: 0,
        Folder: "",
        Urgent: false,
        ShowDate: new Date(),
        EventDate: null,
        ExpirationDate: null,
        RemindDate: null,
        Draft: false,
        Recipients: [],
        RecipientAssignments: [],
        Attachments: [],
        Images: [],
        ImagesData: [],
        Hyperlinks: [],
        MinorEdit: false
    };
    
    constructor(
        private cvApiService: CVApiService,
    ) {}

    public async getAnnouncements(): Promise<BroadcastApiModel>{
        try {
            const response = await this.cvApiService.get<BroadcastApiModel>(`/api/v1/broadcasts/1`);

            if (response.status_code < 200 || response.status_code >= 300) {
                throw response;
            }
            return response.data;
        } catch (error) {
            console.error("Failed getting announcements.");
            throw error;
        }
    }

    public async GetReadReceipt(announcementId: number): Promise<ReadReceiptEntry[]> {
        try {
            const response = await this.cvApiService.get<ReadReceiptEntry[]>(
                "/api/v1/broadcast/" + announcementId + "/read_receipt"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting announcement read receipt.");
            throw err;
        }
    }

    public async GetRecipientAssignments(announcementId: number): Promise<RecipientAssignment[]> {
        try {
            const response = await this.cvApiService.get<RecipientAssignment[]>(
                "/api/v1/broadcast/" + announcementId + "/recipient_assignments"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting announcement receipient assignment.");
            throw err;
        }
    }

    public async AddNewAnnouncement() {
        const recipientsList = [];
        this.save.RecipientAssignments.forEach((recipient)=> {
            recipient.roles.forEach(role => {
            const recipientObj: BroadcastRecipient ={
                BroadcastID: this.save.BroadcastID,
                CompanyID: role.CompanyID,
                GroupLevel: recipient.group.GroupLevel,
                GroupID: recipient.group.GroupID,
                GroupType: 'E',
                RoleID: role.ProfileID
            }
                recipientsList.push(recipientObj);
            })
        })

        this.save.Recipients = recipientsList;

        try {
            const response = await this.cvApiService.post(
                "/api/v1/broadcast",
                this.save
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed adding new announcement.");
            throw err;
        }
    }

    public async EditAnnouncement() {
        const recipientsList = [];
        this.save.RecipientAssignments.forEach((recipient)=> {
            recipient.roles.forEach(role => {
            const recipientObj: BroadcastRecipient ={
                BroadcastID: this.save.BroadcastID,
                CompanyID: role.CompanyID,
                GroupLevel: recipient.group.GroupLevel,
                GroupID: recipient.group.GroupID,
                GroupType: 'E',
                RoleID: role.ProfileID
            }
                recipientsList.push(recipientObj);
            })
        })

        this.save.Recipients = recipientsList;
        try {
            const response = await this.cvApiService.put(
                "/api/v1/broadcast/",
                this.save
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed updating announcement.");
            throw err;
        }
    }

    public async ArchiveAnnouncement(announcementId: number) {
        try {
            const response = await this.cvApiService.delete(
                "/api/v1/broadcast/" + announcementId + "/archive"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed archiving announcement.");
            throw err;
        }
    }

    public async DeleteAnnouncement(announcementId: number) {
        try {
            const response = await this.cvApiService.delete(
                "/api/v1/broadcast/" + announcementId
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed deleting announcement.");
            throw err;
        }
    }

    public async ExportReadReceipt(announcementId: number): Promise<string> {
        try {
            const response = await this.cvApiService.get<string>(
                `/api/v1/broadcast/${announcementId}/export`
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed exporting announcement read receipts.");
            throw err;
        }
    }

    public async ResendNotification(announcementId: number){
        try {
            const response = await this.cvApiService.put(
                "/api/v1/broadcast/" + announcementId + "/resend_notification"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to resend announcement notification.");
            throw err;
        }
    }

    public async GetIFrameAvailability(url: string): Promise<IframeApiModel> {
        try {
            const urlJson = JSON.stringify(url);
            const response = await this.cvApiService.post<IframeApiModel>(
                "/api/v1/iframe_availability", urlJson
            );

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get iframe availability.");
            throw err;
        }
    }

    public changeMode(addMode: boolean = false, editMode: boolean = false, reuseMode: boolean = false){
        const newMode: AddEditArgs= {addMode: addMode, editMode: editMode, reuseMode: reuseMode}
        this.modes.next(newMode)
    }

}
